/* My eSIMs — docs/my-esims.md (data contract), design/my-esims (form).
   Data: GET /apps/pakyo/esims and POST /apps/pakyo/esims/:id/refresh (App Proxy to the fulfilment backend).
   ?demo=1 shows sample eSIMs (real products, made-up numbers) so the page can be reviewed before the backend is live. */
(function () {
  const root = document.querySelector('[data-me-root]');
  if (!root || !window.PT) return;
  const app = root.querySelector('[data-me-app]');
  let PROD = {};
  try { PROD = JSON.parse(document.querySelector('[data-me-products]').textContent || '{}'); } catch (e) { PROD = {}; }
  let ORDERS = {}; // order name → that order's page in the customer account (amounts, payment, receipt)
  try { ORDERS = JSON.parse(document.querySelector('[data-me-orders]').textContent || '{}'); } catch (e) { ORDERS = {}; }

  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const LANG = PT.lang || 'en';
  const DAY = 864e5;
  const flag = iso => iso && /^[A-Za-z]{2}$/.test(iso) ? `https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/1x1/${iso.toLowerCase()}.svg` : '';
  const DEMO_ON = /[?&]demo(=|&|$)/.test(location.search);
  const DEMO_FAIL = /[?&]demo=fail\b/.test(location.search); // preview: every Refresh fails
  const DEMO_CLEAR = /[?&]demo=review\b/.test(location.search); // preview: the held order clears on the first automatic update
  const API = root.dataset.api;
  // One Refresh for the whole page (the backend reads TSIM live, keeps ended cards, and caches a customer's page ~20 s).
  // It stays grey for refresh_available_in seconds (the backend's own window), 15 s if the response doesn't say.
  const COOLDOWN = 15;
  // While an order is held for the payment check, the list reloads itself every minute (visible tab only, for 30 min),
  // so the card turns into the eSIM without a page reload. Only then: each load asks TSIM for every card's usage.
  const POLL = DEMO_CLEAR ? 8e3 : 60e3, POLL_FOR = 30 * 60e3;

  // ---------- formatting ----------
  const nf1 = new Intl.NumberFormat(LANG, { maximumFractionDigits: 1 });
  const nf0 = new Intl.NumberFormat(LANG, { maximumFractionDigits: 0 });
  const mb = v => {
    if (v == null || isNaN(v)) return '';
    if (v >= 1024) return nf1.format(Math.round(v / 102.4) / 10) + ' GB';
    if (v >= 100) return nf0.format(Math.round(v / 10) * 10) + ' MB';
    return nf0.format(Math.max(0, Math.round(v))) + ' MB';
  };
  const speed = kbps => kbps >= 1000 ? nf1.format(kbps / 1000) + ' Mbps' : nf0.format(kbps) + ' kbps';
  const dFmt = new Intl.DateTimeFormat(LANG, { day: 'numeric', month: 'short' });
  const tFmt = new Intl.DateTimeFormat(LANG, { hour: 'numeric', minute: '2-digit' });
  const wFmt = new Intl.DateTimeFormat(LANG, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  const rtf = new Intl.RelativeTimeFormat(LANG, { numeric: 'auto' });
  const date = iso => iso ? dFmt.format(new Date(iso)) : '';
  const when = iso => { const d = new Date(iso); return d.toDateString() === new Date().toDateString() ? tFmt.format(d) : wFmt.format(d); };
  const rel = iso => {
    const s = (new Date(iso) - Date.now()) / 1000, a = Math.abs(s);
    if (a < 60) return rtf.format(0, 'second');
    if (a < 3600) return rtf.format(Math.round(s / 60), 'minute');
    if (a < 86400) return rtf.format(Math.round(s / 3600), 'hour');
    return rtf.format(Math.round(s / 86400), 'day');
  };
  const daysLeft = iso => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / DAY));
  const lines = s => String(s || '').split('\n').filter(Boolean);

  // ---------- product info (from the section's map) ----------
  let BUNDLE_FLAGS = {};
  // variant_id: a number or a GID (gid://shopify/ProductVariant/123); product_handle is optional
  const vid = e => String(e.variant_id == null ? '' : e.variant_id).split('/').pop();
  const info = e => {
    const p = PROD[vid(e)];
    if (p) return p.u ? p : Object.assign({}, p, { u: root.dataset.shop });
    return { h: e.product_handle || '', t: '', v: '', u: e.product_handle ? '/products/' + e.product_handle : root.dataset.shop, n: 1, d: 0 };
  };
  const title = p => String(p.t || '').replace(/(^|\s)eSIM(?=\s|$)/, '$1').replace(/\s{2,}/g, ' ').replace(/^\s*·\s*|\s*·\s*$/g, '').trim() || p.h || 'eSIM';
  const plan = p => (Array.isArray(p.v) ? p.v : [p.v]).filter(Boolean).join(' · ');
  const flagsOf = p => p.b ? (BUNDLE_FLAGS[p.h] || []) : (p.iso ? [p.iso] : []);
  const network = p => p.b ? PT.n('n_dest', p.n) : (p.ops || []).map(PT.op).join(' · ');

  // ---------- state ----------
  const S = { list: null, held: [], error: '', soon: false, tab: 'current', open: {}, method: {}, device: {},
    fetchedAt: 0, until: 0, reloading: false, reloadFail: false, ready: [], fresh: {} };
  const byId = id => (S.list || []).find(e => e.id === id);
  const isPast = e => e.state === 'expired' || e.state === 'terminated';

  // ---------- usage view model (docs/my-esims.md §4) ----------
  const usageView = e => {
    const u = e.usage;
    if (!u) return null;
    if (u.supported === false) return { local: true };
    // the backend keeps no cache: when TSIM can't be reached it says so for that card, with no figures
    const k = u.kind, after = u.after, slow = u.after_kbps;
    // Incomplete figures (docs/my-esims.md §3.2) show as "unavailable", never as blanks or "NaN kbps".
    const num = x => typeof x === 'number' && isFinite(x);
    const incomplete = (k === 'total' || k === 'daily') && (!num(u.left_mb) || !num(u.quota_mb) || !u.quota_mb || (after === 'throttle' && !num(slow)));
    if (u.error || incomplete) return { kind: 'error', error: true, label: PT('me_u_left'), num: '—', hint: '', note: PT('me_u_unavailable') }; // closed button keeps "Data left"
    const v = { kind: k, updated: u.updated_at, bar: null, red: false, needNew: false, label: '', num: '', of: '', note: '', hint: '' };
    const speedWords = slow >= 1000 ? PT('me_speed_fast') : PT('me_speed_slow');
    if (k === 'total') {
      const pct = u.quota_mb ? Math.max(0, Math.min(100, u.left_mb / u.quota_mb * 100)) : 0;
      const out = u.left_mb <= 0;
      v.label = PT('me_u_left'); v.num = mb(u.left_mb); v.of = PT('me_u_of', { total: mb(u.quota_mb) }); v.bar = pct;
      v.red = out || pct < 15; v.needNew = v.red;
      v.hint = out ? (after === 'stop' ? PT('me_u_stopped_short') : PT('me_u_slowed_short')) : PT('me_hint_left', { x: mb(u.left_mb) });
      if (out && after === 'stop') { v.state = PT('me_u_stopped'); v.note = PT('me_u_note_stopped'); }
      else if (out) { v.state = PT('me_u_throttled', { speed: speed(slow) }); v.note = speedWords; }
      else { if (pct < 15) v.state = PT('me_u_low'); v.note = after === 'stop' ? PT('me_u_note_stop') : PT('me_u_note_throttle', { speed: speed(slow) }); }
    } else if (k === 'daily') {
      const out = u.left_mb <= 0, almost = !out && u.left_mb < 50;
      v.label = PT('me_u_left_today');
      v.num = out ? PT('me_u_used_today_done') : almost ? PT('me_u_almost') : mb(u.left_mb);
      v.of = out ? PT('me_u_still_online', { speed: speed(slow) }) : almost ? '' : PT('me_u_of', { total: mb(u.quota_mb) });
      v.bar = out ? 0 : Math.max(0, Math.min(100, u.left_mb / u.quota_mb * 100));
      v.hint = out ? PT('me_hint_slow', { speed: speed(slow) }) : almost ? PT('me_u_almost') : PT('me_hint_left_today', { x: mb(u.left_mb) });
      v.note = (out ? speedWords + ' ' : PT('me_u_note_daily', { quota: mb(u.quota_mb), speed: speed(slow) }) + ' ')
        + (u.reset_at ? PT('me_u_reset', { when: when(u.reset_at), in: rel(u.reset_at) }) : '');
    } else { // daily_unlimited / unlimited: no allowance, only speed
      v.label = PT('me_u_unlimited');
      v.num = PT('me_u_unlimited');
      // TSIM gives no allowance for these, and its rate (if any) is the speed after a fair-use cap we can't see:
      // say nothing about speed rather than promise "full speed" or present the rate as a cap.
      v.of = k === 'unlimited' && u.speed_cap_kbps ? PT('me_u_up_to', { speed: speed(u.speed_cap_kbps) }) : '';
      v.hint = PT('me_u_unlimited');
      if (u.used_mb != null) v.note = PT(k === 'unlimited' ? 'me_u_used_total' : 'me_u_used_today', { x: mb(u.used_mb) });
      v.noRefresh = u.used_mb == null;
    }
    return v;
  };

  // ---------- rendering ----------
  const I = {
    chev: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>',
    down: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 3v12m0 0-4-4m4 4 4-4M5 21h14"/></svg>',
    clock: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 7v5l3 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z"/></svg>',
    check: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
    refresh: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7"/></svg>',
    share: '<svg class="me-app__share" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V3M8 7l4-4 4 4M7 10H5v11h14V10h-2"/></svg>'
  };
  const pillBtn = (id, panel, label, on) => `<button type="button" class="me-pill${on ? ' is-on' : ''}" data-panel="${panel}" data-id="${esc(id)}" aria-expanded="${on}">${esc(label)}${I.chev}</button>`;

  const flagsHtml = (p, big) => {
    const f = flagsOf(p).map(flag).filter(Boolean);
    if (!f.length) return `<span class="me-flag me-flag--blank${big ? '' : ' me-flag--sm'}"></span>`;
    if (f.length === 1) return `<img class="me-flag${big ? '' : ' me-flag--sm'}" src="${f[0]}" alt="">`;
    return `<span class="me-stack${big ? '' : ' me-stack--sm'}">${f.slice(0, 3).map(u => `<img src="${u}" alt="">`).join('')}</span>`;
  };

  const card = e => {
    const p = info(e), open = S.open[e.id] || '', U = e.state === 'active' ? usageView(e) : null;
    const left = e.expires_at ? daysLeft(e.expires_at) : 0;
    const soon = e.state === 'active' && left <= 2;
    const ready = e.state === 'not_installed';
    const status = { active: ['green', PT('me_st_active')], installed: ['blue', PT('me_st_installed')], not_installed: ['orange', PT('me_st_ready')] }[e.state] || ['grey', e.state];
    const banner = ready ? ['ready', I.down, PT('me_bn_ready')]
      : soon ? ['soon', I.clock, PT.n('me_bn_soon', left)]
      : (U && U.kind === 'total' && U.red && e.usage.left_mb <= 0 && e.usage.after === 'stop') ? ['soon', I.clock, PT('me_bn_stopped')] : null;
    const needNew = soon || (U && U.needNew);
    const big = e.state === 'active' ? [String(left), PT.n('me_days_left', left)] : [String(p.d || ''), PT.n('me_days', p.d || 0)];
    const sub = e.state === 'active' ? PT('me_ends', { date: date(e.expires_at) })
      : ready ? (e.install_by ? PT('me_install_by', { date: date(e.install_by) }) : PT('me_not_started'))
      : PT('me_starts_on_connect');
    const primary = ready ? `<button type="button" class="me-primary" data-panel="install" data-id="${esc(e.id)}">${esc(open === 'install' ? PT('me_hide_install') : PT('me_install'))}</button>`
      : needNew ? `<a class="me-primary" href="${esc(p.u)}">${esc(PT('me_buy_new'))}</a>` : '';
    const usageLabel = U ? (U.local ? PT('me_data_left') : (open === 'usage' ? PT('me_data_left') : (U.hint || PT('me_data_left')))) : '';
    return `<article class="me-card me-card--${banner ? banner[0] : 'plain'}${S.fresh[e.id] ? ' is-fresh' : ''}">
      ${banner ? `<div class="me-banner me-banner--${banner[0]}">${banner[1]}<span>${esc(banner[2])}</span></div>` : ''}
      <div class="me-card__top">
        ${flagsHtml(p, true)}
        <div class="me-card__main">
          <div class="me-status me-status--${status[0]}"><i></i>${esc(status[1])}</div>
          <h2 class="me-card__title">${esc(title(p))}</h2>
          <div class="me-card__plan">${esc(plan(p))}${e.count > 1 ? ` · <b>${esc(PT('me_esim_n', { i: e.index, n: e.count }))}</b>` : ''}</div>
        </div>
        <div class="me-card__days">
          <div class="me-card__num${soon ? ' is-red' : ''}"><b>${esc(big[0])}</b><span>${esc(big[1])}</span></div>
          <div class="me-card__sub">${esc(sub)}</div>
        </div>
      </div>
      <div class="me-card__btns">
        ${primary}
        ${U ? pillBtn(e.id, 'usage', usageLabel, open === 'usage') : ''}
        ${pillBtn(e.id, 'details', PT('me_details'), open === 'details')}
      </div>
      ${open === 'install' ? installPanel(e, p) : ''}
      ${open === 'usage' && U ? usagePanel(e, U, p) : ''}
      ${open === 'details' ? detailsPanel(e, p) : ''}
    </article>`;
  };

  const usagePanel = (e, v, p) => {
    if (v.local) {
      return `<div class="me-panel"><div class="me-local">
        <div class="me-local__title">${esc(PT('me_u_local_title'))}</div>
        <p>${esc(PT('me_u_local_text', { carrier: (p.ops || []).map(PT.op).join(' · ') || title(p) }))}</p>
        ${deviceSteps()}
      </div></div>`;
    }
    const barCls = v.kind === 'total' && v.red ? ' is-red' : '';
    return `<div class="me-panel">
      <div class="me-usage">
        <div class="me-usage__row">
          <div>
            <div class="me-usage__label">${esc(v.label)}</div>
            <div class="me-usage__num"><b>${esc(v.num)}</b>${v.of ? ` <span>${esc(v.of)}</span>` : ''}</div>
          </div>
          ${v.noRefresh ? '' : `<div class="me-usage__side"><span class="me-usage__upd" data-synced-at>${esc(syncedAt())}</span></div>`}
        </div>
        ${v.bar != null ? `<div class="me-bar"><i class="${barCls}" style="width:${v.bar}%"></i></div>` : ''}
        ${v.state ? `<div class="me-usage__state${v.kind === 'total' ? ' is-red' : ''}">${esc(v.state)}</div>` : ''}
        ${v.note ? `<p class="me-usage__note">${esc(v.note)}</p>` : ''}
      </div>
      <div class="me-panel__foot"><span>${esc(PT('me_u_lag'))}</span><button type="button" class="me-link" data-device="${esc(e.id)}">${esc(S.device[e.id] ? PT('me_hide_device') : PT('me_check_device'))}</button></div>
      ${S.device[e.id] ? `<div class="me-local me-local--plain">${deviceSteps()}</div>` : ''}
    </div>`;
  };

  const deviceSteps = () => `<ul class="me-steps">
      <li><b>iPhone</b> ${esc(PT('me_dev_ios'))}</li>
      <li><b>Android</b> ${esc(PT('me_dev_android'))}</li>
    </ul><p class="me-fine">${esc(PT('me_dev_note'))}</p>`;

  const installPanel = (e, p) => {
    const ins = e.install || {};
    const lpa = ins.lpa || (ins.smdp && ins.code ? `LPA:1$${ins.smdp}$${ins.code}` : '');
    const ready = e.state === 'not_installed';
    const m = S.method[e.id] || 'qr';
    const tabs = [['qr', PT('me_in_qr')], ['link', PT('me_in_link')], ['manual', PT('me_in_manual')]]
      .map(([k, l]) => `<button type="button" class="me-seg__tab${m === k ? ' is-on' : ''}" data-method="${k}" data-id="${esc(e.id)}">${esc(l)}</button>`).join('');
    let body = '';
    if (m === 'qr') {
      body = `<div class="me-qr"><div class="me-qr__code" data-qr="${esc(lpa)}"></div>
        <div><div class="me-in__title">${esc(PT('me_in_qr_title'))}</div><ol class="me-ol">${lines(PT('me_in_qr_steps')).map(l => `<li>${esc(l)}</li>`).join('')}</ol></div></div>`;
    } else if (m === 'link') {
      body = `<div class="me-in__title">${esc(PT('me_in_link_title'))}</div><p class="me-in__text">${esc(PT('me_in_link_text'))}</p>
        <div class="me-in__btns">
          ${ins.ios_link ? `<a class="me-btn-dark" href="${esc(ins.ios_link)}" rel="noopener">${esc(PT('me_in_ios'))}</a>` : ''}
          ${ins.android_link ? `<a class="me-btn-outline" href="${esc(ins.android_link)}" rel="noopener">${esc(PT('me_in_android'))}</a>` : ''}
        </div><p class="me-fine">${esc(PT('me_in_link_note'))}</p>`;
    } else {
      // confirmation_code: null means there is none (leave the field blank); TSIM hasn't issued one so far
      body = `<p class="me-in__text">${esc(PT(ins.confirmation_code ? 'me_in_manual_text_cc' : 'me_in_manual_text'))}</p>
        ${[['me_in_smdp', ins.smdp], ['me_in_code', ins.code], ['me_in_cc', ins.confirmation_code], ['me_in_lpa', lpa]].filter(([, val]) => val).map(([k, val]) => `<div class="me-copyrow"><div><div class="me-copyrow__k">${esc(PT(k))}</div><code>${esc(val || '')}</code></div><button type="button" class="me-copy" data-copy="${esc(val || '')}">${esc(PT('me_copy'))}</button></div>`).join('')}`;
    }
    const note = ready ? PT('me_in_note_ready') : p.l ? PT('me_in_note_local') : PT('me_in_note_again');
    return `<div class="me-panel">
      <div class="me-in__head"><div class="me-in__h">${esc(ready ? PT('me_in_title_ready') : PT('me_in_title_again'))}</div><div class="me-seg">${tabs}</div></div>
      <div class="me-in__box">${body}</div>
      <p class="me-fine">${esc(note)} <a href="${esc(root.dataset.guide)}">${esc(PT('me_full_guide'))}</a></p>
    </div>`;
  };

  const detailsPanel = (e, p) => {
    const ord = ORDERS[e.order_name] || {};
    const facts = [
      [PT('me_d_order'), e.order_name ? e.order_name + (ord.t ? ' · ' + PT('me_d_total', { total: ord.t }) : '') : ''],
      [PT('me_d_plan'), plan(p)],
      [PT('me_d_network'), network(p)],
      [PT('me_d_iccid'), e.iccid, true]
    ].filter(f => f[1]);
    const canReinstall = e.install && e.state !== 'not_installed';
    const orderUrl = ord.u || (DEMO_ON ? '' : root.dataset.ordersUrl);
    return `<div class="me-panel">
      ${orderUrl ? `<p class="me-orderlink"><a href="${esc(orderUrl)}" data-no-locale>${esc(PT('me_d_view_order'))}</a></p>` : ''}
      <dl class="me-facts">${facts.map(([k, v, copy]) => `<div><dt>${esc(k)}</dt><dd><span>${esc(v)}</span>${copy ? `<button type="button" class="me-copy" data-copy="${esc(v)}">${esc(PT('me_copy'))}</button>` : ''}</dd></div>`).join('')}</dl>
      <div class="me-tips">
        <div><b>${esc(PT('me_d_roam_t'))}</b> ${esc(PT('me_d_roam'))}</div>
        ${e.state !== 'not_installed' ? `<div><b>${esc(PT(p.l ? 'me_d_deleted_t_local' : 'me_d_deleted_t'))}</b> ${esc(p.l ? PT('me_d_deleted_local') : PT('me_d_deleted'))}${canReinstall && !p.l ? ` <button type="button" class="me-link" data-panel="install" data-id="${esc(e.id)}">${esc(PT('me_reinstall'))}</button>` : ''}</div>` : ''}
      </div>
    </div>`;
  };

  const pastRow = e => {
    const p = info(e);
    // terminated (refunded / cancelled at TSIM): "cancelled", dated only by ended_at; expired: "ended <date>"
    const what = plan(p) + (e.count > 1 ? ' · ' + PT('me_esim_n', { i: e.index, n: e.count }) : '');
    const sub = e.state === 'terminated'
      ? (e.ended_at ? PT('me_past_cancelled_on', { plan: what, date: date(e.ended_at) }) : PT('me_past_cancelled', { plan: what }))
      : PT('me_past_sub', { plan: what, date: date(e.ended_at || e.expires_at || e.activated_at || e.ordered_at) });
    return `<div class="me-past">
      ${flagsHtml(p, false)}
      <div class="me-past__main"><div class="me-past__title">${esc(title(p))}</div><div class="me-past__sub">${esc(sub)}</div></div>
      <div class="me-past__order">${esc(e.order_name || '')}</div>
      <a class="me-pill" href="${esc(p.u)}">${esc(PT('me_buy_again'))}</a>
    </div>`;
  };

  // orders held for the pre-provisioning risk check (docs/order-risk.md §8): no eSIM yet, shown on top of "Current"
  const heldCard = h => {
    const ord = ORDERS[h.order_name] || {};
    const orderUrl = ord.u || (DEMO_ON ? '' : root.dataset.ordersUrl);
    const rows = (h.lines || []).map(l => {
      const p = info(l);
      return `<li class="me-review__line">${flagsHtml(p, false)}<div><div class="me-past__title">${esc(title(p))}</div><div class="me-past__sub">${esc(plan(p))}${l.quantity > 1 ? ' · ×' + esc(l.quantity) : ''}</div></div></li>`;
    }).join('');
    return `<article class="me-card me-card--review">
      <div class="me-banner me-banner--review">${I.clock}<span>${esc(PT('me_rv_banner'))}</span></div>
      <div class="me-review">
        <h2 class="me-card__title">${esc(PT('me_rv_title', { order: h.order_name || '' }))}</h2>
        <p class="me-review__text">${esc(PT('me_rv_text'))}</p>
        ${rows ? `<ul class="me-review__lines">${rows}</ul>` : ''}
        ${orderUrl ? `<p class="me-orderlink"><a href="${esc(orderUrl)}" data-no-locale>${esc(PT('me_d_view_order'))}</a></p>` : ''}
      </div>
    </article>`;
  };

  const render = () => {
    if (S.soon) {
      app.innerHTML = `<div class="me-empty"><h2>${esc(PT('me_soon_title'))}</h2><p>${esc(PT('me_soon_text'))}</p><a class="btn-primary" href="${esc(root.dataset.ordersUrl)}" data-no-locale>${esc(PT('me_view_orders'))}</a></div>`;
      return;
    }
    if (S.error) {
      app.innerHTML = `<div class="me-empty"><h2>${esc(PT('me_error_title'))}</h2><p>${esc(PT('me_error_text'))}</p><button type="button" class="btn-primary" data-retry>${esc(PT('me_retry'))}</button></div>`;
      return;
    }
    if (!S.list) {
      app.innerHTML = `<div class="me-loading" role="status"><span></span>${esc(PT('me_loading'))}</div>`;
      return;
    }
    const cur = S.list.filter(e => !isPast(e)), past = S.list.filter(isPast);
    const order = { active: 0, installed: 1, not_installed: 2 };
    const justReady = e => S.ready.includes(e.order_name) ? 0 : 1; // just cleared the payment check: on top, under the notice
    cur.sort((a, b) => (justReady(a) - justReady(b)) || (order[a.state] - order[b.state]) || (new Date(a.expires_at || a.install_by || 0) - new Date(b.expires_at || b.install_by || 0)));
    past.sort((a, b) => new Date(b.expires_at || b.ordered_at || 0) - new Date(a.expires_at || a.ordered_at || 0));
    const tab = (k, label, n) => `<button type="button" class="me-tab${S.tab === k ? ' is-on' : ''}" data-tab="${k}">${esc(label)} <span>${n}</span></button>`;
    const held = S.held || [];
    const heldCount = held.reduce((n, h) => n + (h.lines || []).reduce((m, l) => m + (Number(l.quantity) || 1), 0), 0);
    const body = S.tab === 'past'
      ? (past.length ? `<div class="me-pastlist">${past.map(pastRow).join('')}</div>` : `<p class="me-none">${esc(PT('me_past_empty'))}</p>`)
      : (cur.length || held.length ? held.map(heldCard).join('') + cur.map(card).join('') : `<div class="me-empty"><h2>${esc(PT('me_empty_title'))}</h2><p>${esc(PT('me_empty_text'))}</p><a class="btn-primary" href="${esc(root.dataset.shop)}">${esc(PT('me_empty_btn'))}</a></div>`);
    const toast = S.ready.length && S.tab === 'current'
      ? `<div class="me-toast" role="status">${I.check}<span>${esc(PT('me_ready_toast', { order: S.ready.join(', ') }))}</span><button type="button" class="me-toast__x" data-ready-ok aria-label="${esc(PT('me_close'))}">×</button></div>` : '';
    app.innerHTML = `${DEMO_ON ? `<div class="me-demo">${esc(PT('me_demo'))}</div>` : ''}
      <div class="me-toolbar">
        <div class="me-tabs" role="tablist">${tab('current', PT('me_tab_current'), cur.length + heldCount)}${tab('past', PT('me_tab_past'), past.length)}</div>
        <div class="me-sync"><span class="me-sync__t${S.reloadFail ? ' is-fail' : ''}" data-synced aria-live="polite">${esc(synced())}</span><button type="button" class="me-refresh${S.reloading ? ' is-busy' : ''}" data-reload${S.reloading || coolingDown() ? ' disabled' : ''}>${I.refresh}${esc(PT('me_u_refresh'))}</button></div>
      </div>
      ${toast}<div class="me-list">${body}</div>`;
    drawQr();
  };

  // ---------- QR (qrcode-generator from cdnjs, loaded when first needed) ----------
  let qrLib = null;
  const loadQr = () => qrLib || (qrLib = new Promise((ok, bad) => {
    if (window.qrcode) return ok(window.qrcode);
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcode-generator/1.4.4/qrcode.min.js';
    s.integrity = 'sha512-ZDSPMa/JM1D+7kdg2x3BsruQ6T/JpJo3jWDWkCZsP+5yVyp1KfESqLI+7RqB5k24F7p2cV7i2YHh/890y6P6Sw==';
    s.crossOrigin = 'anonymous';
    s.onload = () => ok(window.qrcode); s.onerror = bad;
    document.head.appendChild(s);
  }));
  const drawQr = () => {
    const els = app.querySelectorAll('[data-qr]');
    if (!els.length) return;
    loadQr().then(qrcode => els.forEach(el => {
      const text = el.getAttribute('data-qr');
      if (!text) return;
      const q = qrcode(0, 'M'); q.addData(text); q.make();
      el.innerHTML = q.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
    })).catch(() => els.forEach(el => { el.textContent = PT('me_in_qr_failed'); }));
  };

  // ---------- data ----------
  // fetched_at is when the backend really read TSIM (a cached answer keeps the original time), so both labels tell the truth
  const syncedAt = () => !S.fetchedAt ? '' : Date.now() - S.fetchedAt < 60e3 ? PT('me_u_just_now') : PT('me_u_updated', { ago: rel(new Date(S.fetchedAt).toISOString()) });
  const synced = () => S.reloadFail ? PT('me_sync_fail') : S.reloading ? '' : syncedAt();
  const coolingDown = () => Date.now() < S.until;

  // silent: keep the page as it is and swap the data in (Refresh button, automatic updates)
  const load = silent => {
    if (silent && (S.reloading || !S.list)) return;
    const heldBefore = (S.held || []).map(h => h.order_name);
    if (silent) { S.reloading = true; S.reloadFail = false; render(); }
    else { S.error = ''; S.soon = false; S.list = null; S.held = []; render(); }
    const got = d => {
      S.reloading = false; S.list = d.esims || []; S.held = Array.isArray(d.held_orders) ? d.held_orders : [];
      S.fetchedAt = Date.parse(d.fetched_at) || Date.now();
      const wait = Number(d.refresh_available_in);
      S.until = Date.now() + 1000 * (isFinite(wait) && wait >= 0 ? Math.max(wait, 3) : COOLDOWN);
      clearTimeout(load.cool); load.cool = setTimeout(render, S.until - Date.now() + 50); // un-grey Refresh when the window ends
      // held orders that came back as eSIMs: say so, and mark their cards
      const still = S.held.map(h => h.order_name);
      const cleared = heldBefore.filter(o => !still.includes(o) && S.list.some(e => e.order_name === o));
      if (cleared.length) {
        S.ready = cleared; S.tab = 'current';
        S.list.forEach(e => { if (cleared.includes(e.order_name)) S.fresh[e.id] = true; });
        setTimeout(() => { S.fresh = {}; }, 3000); // highlight plays once; later renders draw the plain card
      }
    };
    const failed = () => { S.reloading = false; if (silent) S.reloadFail = true; else if (!S.soon) S.error = 'load'; };
    const end = () => { render(); schedule(); };
    if (DEMO_ON) {
      const n = (load.demoRuns = (load.demoRuns || 0) + 1);
      setTimeout(() => { got({ esims: demo().concat(DEMO_CLEAR && n > 1 ? demoCleared() : []), held_orders: DEMO_CLEAR && n > 1 ? [] : demoHeld() }); end(); }, silent ? 700 : 0);
      return;
    }
    fetch(API, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(r => {
        if (r.status === 401) { location.reload(); throw new Error('signed out'); }
        if (r.status === 404 || !(r.headers.get('content-type') || '').includes('json')) { if (silent) throw new Error('soon'); S.soon = true; return null; }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(d => { if (d) got(d); else S.reloading = false; end(); })
      .catch(() => { failed(); end(); });
  };

  let pollT = 0, pollFrom = 0;
  const schedule = () => {
    clearTimeout(pollT); pollT = 0;
    if (!(S.held || []).length) { pollFrom = 0; return; }
    if (!pollFrom) pollFrom = Date.now();
    if (document.hidden || Date.now() - pollFrom > POLL_FOR) return;
    pollT = setTimeout(() => load(true), POLL);
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { clearTimeout(pollT); pollT = 0; return; }
    if ((S.held || []).length && S.list) { pollFrom = 0; load(true); } // back on the page: update now, then every minute again
  });
  setInterval(() => {
    const t = app.querySelector('[data-synced]'); if (t && !S.reloading) t.textContent = synced();
    app.querySelectorAll('[data-synced-at]').forEach(x => { x.textContent = syncedAt(); });
  }, 30e3);

  // ---------- events ----------
  app.addEventListener('click', ev => {
    const t = ev.target.closest('button');
    if (!t || !app.contains(t)) return;
    if (t.dataset.tab) { S.tab = t.dataset.tab; S.open = {}; render(); return; }
    if (t.hasAttribute('data-retry')) { load(); return; }
    if (t.hasAttribute('data-reload')) { if (!coolingDown()) load(true); return; }
    if (t.hasAttribute('data-ready-ok')) { S.ready = []; render(); return; }
    if (t.dataset.panel) {
      const id = t.dataset.id, p = t.dataset.panel;
      S.open = { [id]: S.open[id] === p ? '' : p };
      render(); return;
    }
    if (t.dataset.method) { S.method[t.dataset.id] = t.dataset.method; render(); return; }
    if (t.dataset.device) { S.device[t.dataset.device] = !S.device[t.dataset.device]; render(); return; }
    if (t.hasAttribute('data-copy')) {
      const v = t.getAttribute('data-copy');
      (navigator.clipboard ? navigator.clipboard.writeText(v) : Promise.reject()).then(() => {
        t.textContent = PT('me_copied'); setTimeout(() => { t.textContent = PT('me_copy'); }, 1500);
      }).catch(() => {});
    }
  });

  // ---------- sample data (?demo=1): real products from the section's map, made-up numbers ----------
  const demo = () => {
    const at = d => new Date(Date.now() + d * DAY).toISOString();
    const ins = code => ({ smdp: 'rsp.example-smdp.com', code, ios_link: 'https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=LPA:1$rsp.example-smdp.com$' + code, android_link: 'https://esimsetup.android.com/esim_qrcode_provisioning?carddata=LPA:1$rsp.example-smdp.com$' + code });
    const base = (id, variant, handle, extra) => Object.assign({ id, iccid: '89852000' + String([...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 1e11, 7)).padStart(11, '0'), order_name: '#1042', ordered_at: at(-5), product_handle: handle, variant_id: variant, index: 1, count: 1, install: ins('K2-' + id.toUpperCase() + '-7XM4') }, extra);
    return [
      base('jp1', 50545346576581, 'esim-japan', { state: 'active', activated_at: at(-3), expires_at: at(4.3), usage: { supported: true, kind: 'total', quota_mb: 10240, left_mb: 6963, after: 'throttle', after_kbps: 128, reset_at: null, updated_at: at(-0.12) } }),
      base('hk1', 50546238062789, 'esim-hong-kong', { order_name: '#1047', state: 'active', activated_at: at(-2), expires_at: at(3.2), usage: { supported: true, kind: 'daily', quota_mb: 500, left_mb: 180, after: 'throttle', after_kbps: 128, reset_at: at(0.22), updated_at: at(-0.03) } }),
      base('tr1', 50556721594565, 'esim-turkey', { order_name: '#1051', state: 'active', activated_at: at(-1), expires_at: at(4.1), usage: { supported: true, kind: 'daily', quota_mb: 1024, left_mb: 0, after: 'throttle', after_kbps: 5000, reset_at: at(0.3), updated_at: at(-0.4) } }),
      base('kr1', 50556688367813, 'esim-south-korea-t-c', { order_name: '#1039', state: 'active', activated_at: at(-6), expires_at: at(0.8), usage: { supported: true, kind: 'total', quota_mb: 1024, left_mb: 82, after: 'stop', after_kbps: null, reset_at: null, updated_at: at(-0.06) } }),
      base('uk1', 50562269446341, 'esim-uk-ee-number', { order_name: '#1033', state: 'active', activated_at: at(-10), expires_at: at(20), usage: { supported: false } }),
      base('eu1', 50556484255941, 'esim-europe-ukraine', { order_name: '#1055', state: 'not_installed', index: 1, count: 2, install_by: at(88), usage: null }),
      base('eu2', 50556484255941, 'esim-europe-ukraine', { order_name: '#1055', state: 'installed', index: 2, count: 2, install_by: at(88), usage: null }),
      base('sg1', 50556671983813, 'esim-singapore', { order_name: '#1011', state: 'expired', activated_at: at(-50), expires_at: at(-45), usage: null }),
      base('au1', 50556404531397, 'esim-australia', { order_name: '#1004', state: 'expired', activated_at: at(-82), expires_at: at(-75), usage: null }),
      base('jp2', 50545346576581, 'esim-japan', { order_name: '#1042', state: 'terminated', index: 2, count: 2, ended_at: at(-2), usage: null })
    ].map(e => DEMO_FAIL && e.id === 'jp1' ? Object.assign(e, { usage: { supported: true, error: true } }) : e);
  };

  const demoCleared = () => {
    const ins = code => ({ smdp: 'rsp.example-smdp.com', code, ios_link: 'https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=LPA:1$rsp.example-smdp.com$' + code, android_link: 'https://esimsetup.android.com/esim_qrcode_provisioning?carddata=LPA:1$rsp.example-smdp.com$' + code });
    return [1, 2].map(i => ({ id: 'au6' + i, iccid: '8985200000000060' + i, order_name: '#1060', ordered_at: new Date(Date.now() - 0.05 * DAY).toISOString(), product_handle: 'esim-australia', variant_id: 50556404531397, index: i, count: 2, state: 'not_installed', install_by: new Date(Date.now() + 90 * DAY).toISOString(), usage: null, install: ins('K2-AU6' + i + '-7XM4') }));
  };

  const demoHeld = () => [{ order_name: '#1060', ordered_at: new Date(Date.now() - 0.05 * DAY).toISOString(), state: 'under_review', lines: [{ variant_id: 50556404531397, quantity: 2 }] }];

  // ---------- home-screen app ----------
  // Card under the list (or the sign-in box): on Android/Chrome a button opens the browser's own install prompt (beforeinstallprompt, caught
  // in the section); on iPhone/iPad Safari, where no prompt exists, it says where "Add to Home Screen" is. Phones and
  // tablets only, never inside the installed app; "×" hides it for good on this device. ?app=ios / ?app=prompt previews it.
  const inst = root.querySelector('[data-me-install]');
  if (inst) {
    const ua = navigator.userAgent;
    const PREVIEW = (location.search.match(/[?&]app=(ios|prompt)\b/) || [])[1];
    const standalone = matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
    const ios = (/iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
      && /Safari\//.test(ua) && !/FBAN|FBAV|Instagram|Line\/|MicroMessenger|GSA\//.test(ua); // in-app browsers can't add
    const KEY = 'pk_app_card';
    let off = false;
    try { off = localStorage.getItem(KEY) === 'off'; } catch (e) { /* private mode */ }
    const show = mode => {
      const how = mode === 'ios'
        ? `<p class="me-app__how">${esc(PT('me_app_ios', { share: '\u0001' })).replace('\u0001', I.share)}</p>`
        : `<button type="button" class="btn-primary me-app__btn" data-app-go>${esc(PT('me_app_btn'))}</button>`;
      inst.innerHTML = `<img class="me-app__icon" src="${esc(root.dataset.appIcon)}" alt="" width="44" height="44">
        <div class="me-app__body"><p class="me-app__title">${esc(PT('me_app_title'))}</p><p class="me-app__text">${esc(PT('me_app_text'))}</p>${how}</div>
        <button type="button" class="me-app__x" data-app-x aria-label="${esc(PT('me_close'))}">×</button>`;
      inst.hidden = false;
    };
    if (PREVIEW) show(PREVIEW);
    else if (!standalone && !off && matchMedia('(pointer: coarse)').matches) {
      if (ios) show('ios');
      else if (window.pkInstall) show('prompt');
      else document.addEventListener('pk:install', () => show('prompt'), { once: true });
    }
    inst.addEventListener('click', e => {
      if (e.target.closest('[data-app-x]')) {
        try { localStorage.setItem(KEY, 'off'); } catch (err) { /* private mode */ }
        inst.hidden = true;
      } else if (e.target.closest('[data-app-go]') && window.pkInstall) {
        const p = window.pkInstall;
        window.pkInstall = null;
        p.prompt();
        inst.hidden = true; // the browser's prompt can't be shown twice; if it was dismissed, Chrome offers it again later
      }
    });
    addEventListener('appinstalled', () => { inst.hidden = true; });
  }

  // ---------- start ----------
  const bundles = fetch(root.dataset.bundles).then(r => r.json()).then(list => {
    list.forEach(b => { if (b.url) BUNDLE_FLAGS[b.url.split('/products/')[1]] = (b.flags || []).map(f => f.toUpperCase()); });
  }).catch(() => {});
  if (root.dataset.signedIn || DEMO_ON) {
    app.hidden = false;
    const login = root.querySelector('[data-me-login]');
    if (login && DEMO_ON) login.hidden = true;
    bundles.then(load);
  }
})();
